// Reproduces _order_process.html as a React component.
// The l10n-fill-* class names are intentional hooks for CSS/analytics; the
// values themselves are filled from React state (useShippingLocalization)
// rather than a post-hydration DOM sweep. Initial render matches SSR ("5-7
// Business Days" arrival fallback, "2" days and "Your Location" placeholders)
// until the shipping API resolves.
import { format as formatDate } from "date-fns";
import { Immutable } from "immer";
import React from "react";
import { useFlickity } from "tsi-common-react/src/hooks/useFlickity";
import { CAPTION_LIGHT_CLASS_NAME } from "tsi-common-react/src/utils/typography";
import { t } from "ttag";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { useShippingLocalization } from "../ShippingLocalization";

import styles from "./index.module.scss";

type IProps = Immutable<{
    staticBase: string;
}>;

export const OrderProcessSteps = (props: IProps): React.JSX.Element => {
    const { staticBase } = props;
    // Small-breakpoint carousel for the ordering-process icons (the order-lookup
    // page's `.delivery-flow` list). `watchCSS` leaves Flickity dormant
    // until the CSS enables it at the mobile breakpoint.
    const listRef = React.useRef<HTMLUListElement>(null);
    useFlickity(listRef, {
        options: {
            watchCSS: true,
            draggable: true,
            wrapAround: true,
            prevNextButtons: false,
            pageDots: true,
        },
    });
    const localization = useShippingLocalization();
    const [isEditing, setIsEditing] = React.useState(false);
    const [zip, setZip] = React.useState("");
    const inputRef = React.useRef<HTMLInputElement>(null);
    const updateInFlightRef = React.useRef(false);

    React.useEffect(() => {
        if (isEditing) {
            setZip(localization.zip ?? "");
            inputRef.current?.focus();
        }
    }, [isEditing, localization.zip]);

    const update = async (): Promise<void> => {
        if (zip.length < 5 || updateInFlightRef.current) {
            return;
        }
        updateInFlightRef.current = true;
        try {
            const updated = await localization.setUserZip(zip);
            if (updated) {
                setIsEditing(false);
            } else {
                inputRef.current?.focus();
            }
        } finally {
            updateInFlightRef.current = false;
        }
    };
    return (
        <ul ref={listRef} className={styles.orderProcessList}>
            <li className={styles.icon}>
                <div>
                    <img
                        className="responsive"
                        src={`${staticBase}img/homepage/order-process-step-1.png`}
                        alt="Order Process Step One"
                        loading="lazy"
                        decoding="async"
                    />
                </div>
                <p className={CAPTION_LIGHT_CLASS_NAME}>
                    Simple checkout
                    <br />
                    in under
                </p>
                <p className={CAPTION_LIGHT_CLASS_NAME}>
                    <strong>2 Minutes</strong>
                </p>
            </li>
            <li className={styles.icon}>
                <div>
                    <img
                        className="responsive"
                        src={`${staticBase}img/homepage/order-process-step-2.png`}
                        alt="Order Process Step Two"
                        loading="lazy"
                        decoding="async"
                    />
                </div>
                <p className={CAPTION_LIGHT_CLASS_NAME}>
                    All orders
                    <br />
                    processed within
                </p>
                <p className={CAPTION_LIGHT_CLASS_NAME}>
                    <strong>48 Hours, excluding weekends and holidays</strong>
                </p>
            </li>
            <li className={styles.icon}>
                <div>
                    <img
                        className="responsive"
                        src={`${staticBase}img/homepage/order-process-step-3.png`}
                        alt="Order Process Step Three"
                        loading="lazy"
                        decoding="async"
                    />
                </div>
                <p className={CAPTION_LIGHT_CLASS_NAME}>
                    Ships via UPS in as <br />
                    little as{" "}
                    <span className="l10n-fill-shipping-days-in-transit">
                        {localization.daysInTransit || 2}
                    </span>{" "}
                    days to
                </p>
                <button
                    className={concatClassNames([
                        styles.userLocation,
                        CAPTION_LIGHT_CLASS_NAME,
                    ])}
                    onClick={(event) => {
                        if (!isEditing) {
                            event.preventDefault();
                            setIsEditing(true);
                        }
                    }}
                >
                    {isEditing ? (
                        <input
                            ref={inputRef}
                            className="l10n-zip-input"
                            id="location-zip-code"
                            placeholder="ZIP CODE"
                            value={zip}
                            onClick={(event) => {
                                event.stopPropagation();
                            }}
                            onBlur={() => {
                                void update();
                            }}
                            onChange={(event) => {
                                setZip(event.target.value);
                            }}
                            onKeyDown={(event) => {
                                if (event.key === "Enter") {
                                    void update();
                                }
                            }}
                        />
                    ) : (
                        <>
                            <strong
                                className={concatClassNames([
                                    styles.userLocationValue,
                                    "l10n-fill-user-location",
                                    "l10n-editable",
                                ])}
                            >
                                {localization.locationDisplayName ??
                                    "Your Location"}
                            </strong>
                            <span className="ada-screenreader-only">
                                . Change location.
                            </span>
                            <label htmlFor="location-zip-code">ZIP CODE</label>
                        </>
                    )}
                </button>
            </li>
            <li className={styles.icon}>
                <div>
                    <img
                        className="responsive"
                        src={`${staticBase}img/homepage/order-process-step-4.png`}
                        alt="Order Process Step Four"
                        loading="lazy"
                        decoding="async"
                    />
                </div>
                <p className={CAPTION_LIGHT_CLASS_NAME}>
                    At your doorstep <br />
                    as soon as
                </p>
                <p className={CAPTION_LIGHT_CLASS_NAME}>
                    <strong
                        className="l10n-fill-shipping-arrival-date"
                        data-format="MMMM do"
                    >
                        {localization.arrivalDate
                            ? formatDate(localization.arrivalDate, "MMMM do")
                            : t`5-7 Business Days`}
                    </strong>
                </p>
            </li>
        </ul>
    );
};
